/**
 * Centralized telemetry configuration to avoid hardcoded values across codebase.
 *
 * All sampling rates and batch processor settings are defined here as single
 * source of truth. This allows changing telemetry behavior with one edit.
 */

/**
 * Default sampling rates per environment.
 * Can be overridden via OTEL_SAMPLING_RATE environment variable.
 */
export const DEFAULT_SAMPLING_RATES: Record<string, number> = {
    development: 1.0, // 100% — capture all traces in dev
    staging: 0.5, // 50% — sample half in staging to manage costs
    production: 0.2, // 20% — sample 1-in-5 traces in production
}

/**
 * Default batch processor delays (ms) per environment.
 * Lower delays in production mean faster trace export; higher delays in dev reduce overhead.
 */
export const DEFAULT_BATCH_DELAYS: Record<string, number> = {
    development: 5_000, // 5s — reduce unnecessary flushes in dev
    staging: 5_000, // 5s — standard batch delay
    production: 3_000, // 3s — lower latency for faster observability
}

/**
 * Resolves the sampling rate for a given environment.
 * Prefers environment variable override, falls back to DEFAULT_SAMPLING_RATES.
 */
export function getSamplingRate(environment: string): number {
    const override =
        typeof process !== 'undefined'
            ? process.env.OTEL_SAMPLING_RATE
            : undefined
    if (override !== undefined) {
        const rate = Number(override)
        return Number.isFinite(rate) ? Math.max(0, Math.min(1, rate)) : 1.0
    }
    const envKey = environment.toLowerCase()
    return DEFAULT_SAMPLING_RATES[envKey] ?? 1.0
}

/**
 * Resolves the batch processor delay (ms) for a given environment.
 * Prefers environment variable override, falls back to DEFAULT_BATCH_DELAYS.
 */
export function getBatchDelay(environment: string): number {
    const override =
        typeof process !== 'undefined'
            ? process.env.OTEL_BATCH_DELAY_MS
            : undefined
    if (override !== undefined) {
        const delay = Number(override)
        return Number.isFinite(delay) ? Math.max(1_000, delay) : 5_000
    }
    const envKey = environment.toLowerCase()
    return DEFAULT_BATCH_DELAYS[envKey] ?? 5_000
}

/**
 * Parses the standard OTLP headers env format (`OTEL_EXPORTER_OTLP_HEADERS`):
 * comma-separated `key=value` pairs, e.g. `org=acme,x-api-key=secret`.
 */
export function parseOtlpHeaders(raw?: string): Record<string, string> {
    if (!raw) return {}
    return Object.fromEntries(
        raw
            .split(',')
            .map((pair) => pair.split('='))
            .filter(([key, value]) => key?.trim() && value !== undefined)
            .map(([key, ...rest]) => [
                key.trim(),
                decodeURIComponent(rest.join('=').trim()),
            ]),
    )
}
